(ns app.front.view.application.transfer-of-permit
  (:require [app.domain.form.model :as form-model]
            [app.domain.party-role.model :as role-model]
            [app.domain.party.model :as party-model]
            [app.domain.transfer-of-permit.model :as top-model]
            [app.front.entities :as entities]
            [app.front.process :as process]
            [app.front.ui.table :as ui-table]
            [app.front.view.workspace.section :as ws-section]
            [app.front.view.workspace.subs :as ws-subs]
            [clojure.string :as str]
            [core.front.event :refer [<sub]]
            [core.front.href :as href]
            [potpuri.core :as p]
            [re-frame.core-instrumented :as rf]
            [tilakone.core :as tk]))

(defn party-entities-handler-fn [& role-keys]
  (fn [[app-id ctx] _]
    (->> {:entity/type   :entity.type/party
          :entity/parent app-id}
         (entities/find-entities ctx)
         (filter #(party-model/has-any-role? role-keys %)))))

(rf/reg-sub ::existing-party-entities
  :<- [::ws-subs/application-id]
  :<- [::entities/context]
  (party-entities-handler-fn :role/applicant :role/property-owner))

(rf/reg-sub ::incoming-party-entities
  :<- [::ws-subs/application-id]
  :<- [::entities/context]
  (party-entities-handler-fn :role/new-applicant :role/new-property-owner))

(rf/reg-sub ::approval-field-entities
  :<- [::ws-subs/application-id]
  :<- [::entities/context]
  (fn [[app-id ctx] _]
    (entities/find-entities ctx {:entity/type   :entity.type/form-field
                                 :form.field/id top-model/approval-field-id
                                 :entity/parent app-id})))

(rf/reg-sub ::transfer-selection-field-entities
  :<- [::ws-subs/application-id]
  :<- [::entities/context]
  (fn [[app-id ctx] _]
    (entities/find-entities ctx {:entity/type   :entity.type/form-field
                                 :form.field/id top-model/removal-choice-field-id
                                 :entity/parent app-id})))



(defn form-field->party-id+transfer-status [form-field]
  (let [party (-> form-field :form.field/party)]
    [(-> party :db/id)
     (let [signed-by (form-model/determine-signed-by form-field)]
       (cond (and (:party/user party)
                  (= signed-by (-> party :party/user :db/id)))
             :transfer-of-permit/ready
             signed-by
             :transfer-of-permit/signature-mismatch
             :else
             :transfer-of-permit/waiting-for-signature))]))

(rf/reg-sub ::approval-status
  :<- [::approval-field-entities]
  (fn [ents _]
    (->> ents
         (map form-field->party-id+transfer-status)
         (into {}))))

(rf/reg-sub ::removal-choices
  :<- [::transfer-selection-field-entities]
  (fn [ents _]
    (->> ents
         (map (juxt (comp :db/id :form.field/party)
                    :form.field/value))
         (into {}))))

(rf/reg-sub ::form-ids
  :<- [::ws-subs/application-id]
  :<- [::entities/context]
  (fn [[app-id ctx] _]
    (->> {:entity/type   :entity.type/form
          :entity/parent app-id}
         (entities/find-entities ctx)
         (filter #(->> %
                       :form/form-fields
                       (map :form.field/id)
                       (some (partial = top-model/approval-field-id))))
         (map (juxt (comp :db/id :form/party)
                    :db/id))
         (into {}))))


(defn translate-and-join [lang party-roles]
  (->> party-roles
       (map #(role-model/party-role-name % lang))
       sort
       (str/join ", ")))

(rf/reg-sub ::existing-parties
  :<- [::existing-party-entities]
  :<- [::approval-status]
  :<- [:language]
  :<- [::form-ids]
  :<- [::removal-choices]
  (fn [[parties approval-status lang form-ids removal-choices] _]
    (->> parties
         (map (fn [{:party/keys [user party-roles]
                    party-id    :db/id
                    :as         party}]
                {:db/id                 party-id
                 :name                  (party-model/simple-name party lang)
                 :roles                 (translate-and-join lang party-roles)
                 :user-id               (-> user :db/id)
                 :form-id               (get form-ids party-id)
                 :status                (get approval-status party-id)
                 :status-after-transfer (let [removal-choice (get removal-choices party-id)]
                                          (cond
                                            (= top-model/removal-choice-remove removal-choice)
                                            :transfer-of-permit/will-be-removed
                                            (= top-model/removal-choice-remain removal-choice)
                                            :transfer-of-permit/will-remain
                                            :else
                                            nil))})))))

(rf/reg-sub ::state-machine
  :<- [::process/current-ws-process-states]
  (fn [process-states _]
    (-> process-states :entity.type/application)))

(rf/reg-sub ::transfer-role-mapping
  :<- [::state-machine]
  (fn [state-machine _]
    (->> state-machine
         (map ::tk/enter)
         (mapcat ::tk/actions)
         (filter #(-> % first (= :process.action/alter-parties-per-transfer-of-permit)))
         first ; if there are multiple alter-actions, just pick one - undefined behaviour
         second)))

(rf/reg-sub ::incoming-parties
  :<- [::incoming-party-entities]
  :<- [::approval-status]
  :<- [:language]
  :<- [::transfer-role-mapping]
  :<- [::form-ids]
  :<- [::ws-subs/party-roles]
  (fn [[parties approval-status lang transfer-role-mapping form-ids party-role-entities] _]
    (when transfer-role-mapping
      (->> parties
           (map (fn [{:party/keys [email party-roles]
                      party-state :domain/state
                      party-id    :db/id
                      :as         party}]
                  {:db/id     party-id
                   :email     email
                   :status    (case party-state
                                :party.state/active
                                (get approval-status party-id)
                                :party.state/pending
                                :transfer-of-permit/pending-invite
                                :party.state/declined
                                :transfer-of-permit/declined)
                   :name      (some-> party (party-model/simple-name lang))
                   :form-id   (get form-ids party-id)
                   :new-roles (->> party-roles
                                   (keep (fn [{old-role-key :party-role/key}]
                                           (when-let [new-role-key (get transfer-role-mapping old-role-key)]
                                             (p/find-first party-role-entities {:party-role/key new-role-key}))))
                                   (translate-and-join lang))
                   :signed-by (get approval-status party-id)}))))))

(rf/reg-sub ::show-section?
  :<- [::approval-field-entities]
  (fn [ents] (-> ents seq boolean)))

(defn make-link [tr workspace]
  {:make-href   (fn [{:keys [form-id]}]
                  (href/href :view/form
                             {:workspace-id (-> workspace :workspace/id)
                              :form-id      form-id}))
   :description (tr [:transfer-of-permit/open-form])})

(defn transfer-of-permit-section [{:keys [application workspace tr] :as state}]
  (when (<sub [::show-section?])
    [ws-section/section
     application
     :transfer-of-permit
     state
     :category/transfer-of-permit
     true
     [:div
      [:p (tr [:transfer-of-permit/existing-parties-description])]
      [ui-table/modern-responsive-table
       {:cols  [[(tr [:user-management/name])
                 :name
                 {:width "30%"}]
                [(tr [:party/party-roles])
                 :roles]
                [(tr [:transfer-of-permit/status-after-transfer])
                 (fn [{:keys [status-after-transfer]}]
                   (if status-after-transfer
                     (tr [status-after-transfer])
                     "-"))
                 {:width "220px"}]
                [(tr [:transfer-of-permit/status])
                 #(some-> (:status %)
                          vector
                          tr)
                 {:width "220px"}]]
        :items (<sub [::existing-parties])
        :link  (make-link tr workspace)
        :tr    tr}]
      [:p.mt-4 (tr [:transfer-of-permit/new-parties-description])]
      [ui-table/modern-responsive-table
       {:cols  [[(tr [:transfer-of-permit/name-or-email])
                 (some-fn :name :email)
                 {:width "30%"}]
                [(tr [:transfer-of-permit/new-roles])
                 :new-roles]
                [(tr [:transfer-of-permit/status])
                 #(some-> (:status %)
                          vector
                          tr)
                 {:width "220px"}]]
        :items (<sub [::incoming-parties])
        :link  (make-link tr workspace)
        :tr    tr}]]]))
